import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../componentes/ui";
import {
  useAlterarPerfilDeAcesso,
  useApagarPerfil,
  useCatalogoDePermissoes,
  useCriarPerfil,
  useDuplicarPerfil,
  usePerfis,
  permissoesSensiveis,
  type PerfilDeAcesso,
  type PermissaoDoPerfil,
} from "../nucleo/acesso";
import { mensagemDoConsole } from "../nucleo/console";
import { GRUPOS, menuPrevisto, POR_CHAVE, rotuloDaPermissao } from "../nucleo/permissoesDoConsole";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

/**
 * Perfis de acesso: além dos papéis de sistema, a administração cria perfis com um
 * subconjunto das permissões do lado certo. Um perfil de cliente nunca recebe permissão interna
 * — a lista as mostra desabilitadas e, se alguém insistir, o banco recusa.
 */
export function PerfisDeAcesso() {
  useTitulo("Perfis de acesso");
  const sessao = useSessao();

  if (!podeNaSessao(sessao.data, "perfil:gerenciar")) {
    return (
      <Aviso tom="marca" data-testid="perfis-sem-permissao">
        Os perfis de acesso são administrados pela equipe interna com essa permissão.
      </Aviso>
    );
  }
  return (
    <div data-testid="perfis-de-acesso">
      <h1 className="titulo-pagina">Perfis de acesso</h1>
      <p className="lead">
        Crie perfis com só as permissões que cada grupo precisa. Eles são atribuídos às pessoas como
        os papéis fixos, e a mudança vale no pedido seguinte de quem já tem o perfil.
      </p>
      <NovoPerfil />
      <ListaDePerfis />
    </div>
  );
}

function NovoPerfil() {
  const catalogo = useCatalogoDePermissoes();
  const criar = useCriarPerfil();
  const voltarParaHome = useVoltarParaHome();
  const [aberto, setAberto] = useState(false);

  if (!aberto) {
    return (
      <div className="mb-6">
        <Botao onClick={() => setAberto(true)} data-testid="novo-perfil">
          Novo perfil
        </Botao>
      </div>
    );
  }
  if (catalogo.isPending) return <p role="status">Carregando as permissões…</p>;
  if (catalogo.isError) return <Aviso tom="erro">{mensagemDoConsole(catalogo.error)}</Aviso>;

  return (
    <section className="mb-6" aria-labelledby="titulo-novo-perfil">
      <h2 className="titulo-secao" id="titulo-novo-perfil">
        Novo perfil
      </h2>
      <EditorDePerfil
        catalogo={catalogo.data}
        enviando={criar.isPending}
        erro={criar.isError ? mensagemDoConsole(criar.error) : null}
        rotuloDoBotao="Criar perfil"
        aoSalvar={(valores) =>
          criar.mutate(
            {
              nome: valores.nome,
              descricao: valores.descricao || null,
              lado: valores.lado,
              permissoes: valores.permissoes,
            },
            {
                onSuccess: () => {
                  setAberto(false);
                  voltarParaHome("Perfil criado.");
                },
              },
          )
        }
        aoCancelar={() => setAberto(false)}
      />
    </section>
  );
}

interface ValoresDoPerfil {
  nome: string;
  descricao: string;
  lado: "interno" | "cliente";
  permissoes: string[];
}

function EditorDePerfil({
  catalogo,
  inicial,
  ladoFixo = false,
  enviando,
  erro,
  rotuloDoBotao,
  aoSalvar,
  aoCancelar,
  usuarios = 0,
  usuariosAtivos = 0,
  usuariosNomes = [],
}: {
  catalogo: readonly PermissaoDoPerfil[];
  inicial?: ValoresDoPerfil;
  /** O lado não muda depois de criado. */
  ladoFixo?: boolean;
  enviando: boolean;
  erro: string | null;
  rotuloDoBotao: string;
  aoSalvar: (valores: ValoresDoPerfil) => void;
  aoCancelar: () => void;
  usuarios?: number;
  usuariosAtivos?: number;
  usuariosNomes?: string[];
}) {
  const [nome, setNome] = useState(inicial?.nome ?? "");
  const [descricao, setDescricao] = useState(inicial?.descricao ?? "");
  const [lado, setLado] = useState<"interno" | "cliente">(inicial?.lado ?? "cliente");
  const [escolhidas, setEscolhidas] = useState<Set<string>>(new Set(inicial?.permissoes ?? []));
  const agrupadas = GRUPOS.map((grupo) => ({
    grupo,
    itens: catalogo.filter((item) =>
      (POR_CHAVE.get(item.chave)?.grupo ?? "Outras permissões") === grupo
    ),
  })).filter((item) => item.itens.length > 0);

  function alternar(chave: string) {
    setEscolhidas((atual) => {
      const novo = new Set(atual);
      if (novo.has(chave)) novo.delete(chave);
      else novo.add(chave);
      return novo;
    });
  }

  function trocarLado(novoLado: "interno" | "cliente") {
    setLado(novoLado);
    if (novoLado === "cliente") {
      // O que é interno não segue para um perfil de cliente.
      const permitidas = new Set(catalogo.filter((item) => item.cliente_pode).map((i) => i.chave));
      setEscolhidas((atual) => new Set([...atual].filter((chave) => permitidas.has(chave))));
    }
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    aoSalvar({ nome: nome.trim(), descricao: descricao.trim(), lado, permissoes: [...escolhidas] });
  }

  return (
    <form onSubmit={enviar} noValidate data-testid="editor-de-perfil">
      <Campo
        rotulo="Nome do perfil"
        value={nome}
        onChange={(evento) => setNome(evento.target.value)}
        obrigatorio
        data-testid="perfil-nome"
      />
      <CampoLongo
        rotulo="Descrição (opcional)"
        value={descricao}
        onChange={(evento) => setDescricao(evento.target.value)}
        data-testid="perfil-descricao"
      />
      {ladoFixo ? (
        <p className="campo__ajuda">
          Perfil {lado === "cliente" ? "de cliente" : "interno"} — o lado não muda depois de criado.
        </p>
      ) : (
        <>
          <label className="campo__rotulo" htmlFor="perfil-lado">
            Para quem é
          </label>
          <select
            id="perfil-lado"
            className="campo__controle mb-3"
            value={lado}
            onChange={(evento) => trocarLado(evento.target.value as "interno" | "cliente")}
            data-testid="perfil-lado"
          >
            <option value="cliente">Pessoas das organizações clientes</option>
            <option value="interno">Equipe interna da GEOvendas</option>
          </select>
        </>
      )}
      {usuarios > 0 ? <Aviso tom="atencao" className="mb-3">Usado por {quemUsa(usuarios, usuariosAtivos, usuariosNomes)}. Salvar muda o acesso de todas na próxima ação.</Aviso> : null}
      <div className="grid gap-3 md:grid-cols-2">
        {agrupadas.map(({ grupo, itens }) => <fieldset key={grupo} className="mb-3">
        <legend className="campo__rotulo">{grupo}</legend>
        <div className="grid gap-2">
          {itens.map((permissao) => {
            const bloqueada = lado === "cliente" && !permissao.cliente_pode;
            return (
              <label
                key={permissao.chave}
                className="flex items-start gap-2 text-sm"
                title={bloqueada ? "Permissão interna: não vale para perfil de cliente." : undefined}
              >
                <input
                  type="checkbox"
                  checked={escolhidas.has(permissao.chave)}
                  disabled={bloqueada || permissao.chave === "credencial:revelar"}
                  onChange={() => alternar(permissao.chave)}
                  data-testid={`permissao-${permissao.chave}`}
                />
                <span>
                  <b>{POR_CHAVE.get(permissao.chave)?.rotulo ?? permissao.descricao}</b>
                  <span className="celula-secundaria">{permissao.descricao}</span>
                  {permissao.chave === "credencial:revelar" ? <span className="celula-secundaria">Exclusiva do papel fixo de Infraestrutura.</span> : null}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>)}
      </div>
      {lado === "interno" ? <Aviso tom="marca" className="mb-3" aria-live="polite">
        No menu de quem tem este perfil: {menuPrevisto(escolhidas).join(", ") || "nenhum item"}.
      </Aviso> : null}
      {erro ? (
        <Aviso tom="erro" className="mb-3" data-testid="erro-do-perfil">
          {erro}
        </Aviso>
      ) : null}
      <div className="flex gap-2">
        <Botao type="submit" disabled={enviando || nome.trim() === ""} data-testid="salvar-perfil">
          {enviando ? "Salvando…" : rotuloDoBotao}
        </Botao>
        <Botao variante="fantasma" onClick={aoCancelar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function quemUsa(usuarios: number, ativos: number, nomes: readonly string[]): string {
  if (nomes.length === 0) return ativos === 0 ? `${usuarios} pessoa(s) desativada(s)` : `${ativos} pessoa(s)`;
  const resto = ativos - nomes.length;
  return resto > 0 ? `${nomes.join(", ")} e mais ${resto} pessoa(s)` : nomes.join(", ");
}

function retiradasNaCopia(perfil: PerfilDeAcesso, sensiveis: ReadonlySet<string>): string[] {
  return perfil.permissoes.filter((chave) => sensiveis.has(chave));
}

function ListaDePerfis() {
  const perfis = usePerfis();
  const catalogo = useCatalogoDePermissoes();
  const [editando, setEditando] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const alterar = useAlterarPerfilDeAcesso();
  const apagar = useApagarPerfil();
  const duplicar = useDuplicarPerfil();
  const sensiveis = permissoesSensiveis(catalogo.data);
  const [origemDaCopia, setOrigemDaCopia] = useState<string | null>(null);

  function duplicarPerfil(perfil: PerfilDeAcesso) {
    if (duplicar.isPending) return;
    const nomes = new Set((perfis.data ?? []).map((item) => item.nome.toLocaleLowerCase("pt-BR")));
    let nome = `${perfil.nome} (cópia)`;
    let numero = 2;
    while (nomes.has(nome.toLocaleLowerCase("pt-BR"))) {
      nome = `${perfil.nome} (cópia ${numero})`;
      numero += 1;
    }
    setOrigemDaCopia(perfil.chave);
    duplicar.mutate({ origem: perfil.chave, nome });
  }

  // O resultado aparece junto do botão que o pediu, com o nome da cópia e o que ficou de fora.
  function resultadoDaCopia(origem: string) {
    if (origemDaCopia !== origem) return null;
    if (duplicar.isError) {
      return <Aviso tom="erro" className="mt-3" data-testid={`erro-da-copia-${origem}`}>{mensagemDoConsole(duplicar.error)}</Aviso>;
    }
    if (!duplicar.isSuccess) return null;
    const { nome, retiradas } = duplicar.data;
    return (
      <p role="status" className="mt-3" data-testid={`copia-de-${origem}`}>
        Cópia “{nome}” criada como perfil editável.
        {retiradas.length > 0 ? ` Ficaram de fora: ${retiradas.map(rotuloDaPermissao).join(", ")}.` : ""}
      </p>
    );
  }

  if (perfis.isPending || catalogo.isPending) return <p role="status">Carregando os perfis…</p>;
  if (perfis.isError) return <Aviso tom="erro">{mensagemDoConsole(perfis.error)} <Botao tamanho="pequeno" variante="fantasma" onClick={() => void perfis.refetch()}>Tentar de novo</Botao></Aviso>;
  if (catalogo.isError) return <Aviso tom="erro">{mensagemDoConsole(catalogo.error)} <Botao tamanho="pequeno" variante="fantasma" onClick={() => void catalogo.refetch()}>Tentar de novo</Botao></Aviso>;

  const internos = perfis.data.filter((perfil) => perfil.lado === "interno");
  const outros = perfis.data.filter((perfil) => perfil.lado === "cliente");

  return (
    <>
      <section className="mb-6" aria-labelledby="titulo-perfis-criados">
        <h2 className="titulo-secao" id="titulo-perfis-criados">
          Perfis da equipe
        </h2>
        {internos.length === 0 ? (
          <Aviso tom="marca">Nenhum perfil interno cadastrado ainda.</Aviso>
        ) : (
          <div className="grid gap-3">
            {internos.map((perfil) => (
              <Cartao key={perfil.chave} data-testid={`perfil-${perfil.chave}`}>
                {editando === perfil.chave ? (
                  <EditorDePerfil
                    catalogo={catalogo.data}
                    ladoFixo
                    usuarios={perfil.usuarios}
                    usuariosAtivos={perfil.usuarios_ativos}
                    usuariosNomes={perfil.usuarios_nomes}
                    inicial={{
                      nome: perfil.nome,
                      descricao: perfil.descricao ?? "",
                      lado: perfil.lado,
                      permissoes: perfil.permissoes,
                    }}
                    enviando={alterar.isPending}
                    erro={alterar.isError ? mensagemDoConsole(alterar.error) : null}
                    rotuloDoBotao="Salvar alterações"
                    aoSalvar={(valores) =>
                      alterar.mutate(
                        {
                          chave: perfil.chave,
                          nome: valores.nome,
                          descricao: valores.descricao || null,
                          permissoes: valores.permissoes,
                        },
                        { onSuccess: () => setEditando(null) },
                      )
                    }
                    aoCancelar={() => setEditando(null)}
                  />
                ) : (
                  <ResumoDoPerfil
                    perfil={perfil}
                    sensiveis={sensiveis}
                    apagando={apagar.isPending}
                    duplicando={duplicar.isPending}
                    aoEditar={perfil.personalizado ? () => setEditando(perfil.chave) : undefined}
                    aoApagar={perfil.personalizado ? () => setConfirmando(perfil.chave) : undefined}
                    aoDuplicar={() => duplicarPerfil(perfil)}
                  />
                )}
                {confirmando === perfil.chave ? <Aviso tom="atencao" className="mt-3">
                  <p>Apagar o perfil {perfil.nome}? Pessoas com este perfil devem ser transferidas antes.</p>
                  <Botao tamanho="pequeno" variante="fantasma" onClick={() => setConfirmando(null)}>Cancelar</Botao>{" "}
                  <Botao tamanho="pequeno" disabled={apagar.isPending} onClick={() => apagar.mutate(perfil.chave, { onSuccess: () => setConfirmando(null) })}>Confirmar exclusão de {perfil.nome}</Botao>
                </Aviso> : null}
                {resultadoDaCopia(perfil.chave)}
              </Cartao>
            ))}
          </div>
        )}
        {apagar.isError ? (
          <Aviso tom="erro" className="mt-3" data-testid="erro-ao-apagar-perfil">
            {mensagemDoConsole(apagar.error)}
          </Aviso>
        ) : null}
      </section>

      <section className="mb-6" aria-labelledby="titulo-papeis-fixos">
        <h2 className="titulo-secao" id="titulo-papeis-fixos">
          Perfis dos clientes
        </h2>
        <p className="lead">Perfis do lado do cliente continuam separados dos acessos da equipe interna.</p>
        <ul className="grid gap-2 md:grid-cols-2">
          {outros.filter((perfil) => !perfil.personalizado).map((perfil) => (
            <li key={perfil.chave} className="text-sm">
              <b>{perfil.nome}</b> <Chip estado="neutro" semPonto>{perfil.lado === "cliente" ? "cliente" : "interno"}</Chip>{" "}
              · {perfil.permissoes.length} permissões · {perfil.usuarios} pessoas
              <span className="celula-secundaria"> Perfil de sistema, não editável.</span>{" "}
              <Botao tamanho="pequeno" variante="fantasma" disabled={duplicar.isPending} onClick={() => duplicarPerfil(perfil)}>Duplicar</Botao>
              {resultadoDaCopia(perfil.chave)}
            </li>
          ))}
        </ul>
        <div className="mt-3 grid gap-3">
          {outros.filter((perfil) => perfil.personalizado).map((perfil) => (
            <Cartao key={perfil.chave} data-testid={`perfil-${perfil.chave}`}>
              {editando === perfil.chave ? (
                <EditorDePerfil
                  catalogo={catalogo.data}
                  ladoFixo
                  usuarios={perfil.usuarios}
                  usuariosAtivos={perfil.usuarios_ativos}
                  usuariosNomes={perfil.usuarios_nomes}
                  inicial={{ nome: perfil.nome, descricao: perfil.descricao ?? "", lado: perfil.lado, permissoes: perfil.permissoes }}
                  enviando={alterar.isPending}
                  erro={alterar.isError ? mensagemDoConsole(alterar.error) : null}
                  rotuloDoBotao="Salvar alterações"
                  aoSalvar={(valores) => alterar.mutate({ chave: perfil.chave, nome: valores.nome, descricao: valores.descricao || null, permissoes: valores.permissoes }, { onSuccess: () => setEditando(null) })}
                  aoCancelar={() => setEditando(null)}
                />
              ) : <ResumoDoPerfil
                perfil={perfil}
                sensiveis={sensiveis}
                apagando={apagar.isPending}
                duplicando={duplicar.isPending}
                aoEditar={() => setEditando(perfil.chave)}
                aoApagar={() => setConfirmando(perfil.chave)}
                aoDuplicar={() => duplicarPerfil(perfil)}
              />}
              {confirmando === perfil.chave ? <Aviso tom="atencao" className="mt-3">
                <p>Apagar o perfil {perfil.nome}? Pessoas com este perfil devem ser transferidas antes.</p>
                <Botao tamanho="pequeno" variante="fantasma" onClick={() => setConfirmando(null)}>Cancelar</Botao>{" "}
                <Botao tamanho="pequeno" disabled={apagar.isPending} onClick={() => apagar.mutate(perfil.chave, { onSuccess: () => setConfirmando(null) })}>Confirmar exclusão de {perfil.nome}</Botao>
              </Aviso> : null}
              {resultadoDaCopia(perfil.chave)}
            </Cartao>
          ))}
        </div>
      </section>
    </>
  );
}

function ResumoDoPerfil({
  perfil,
  sensiveis,
  apagando,
  duplicando,
  aoEditar,
  aoApagar,
  aoDuplicar,
}: {
  perfil: PerfilDeAcesso;
  /** Permissões que a cópia não leva, como o catálogo da API as marca. */
  sensiveis: ReadonlySet<string>;
  apagando: boolean;
  duplicando: boolean;
  aoEditar?: () => void;
  aoApagar?: () => void;
  aoDuplicar: () => void;
}) {
  const retiradas = retiradasNaCopia(perfil, sensiveis);
  return (
    <>
      <p className="m-0">
        <b>{perfil.nome}</b> <Chip estado="neutro" semPonto>{perfil.lado === "cliente" ? "cliente" : "interno"}</Chip>
      </p>
      {perfil.descricao ? <p className="cartao__texto">{perfil.descricao}</p> : null}
      {!perfil.personalizado ? (
        <p className="celula-secundaria">
          Perfil de sistema, não editável. Você pode duplicá-lo para criar um perfil editável
          {retiradas.length > 0 ? `; a cópia não inclui ${retiradas.map(rotuloDaPermissao).join(", ")}.` : "."}
        </p>
      ) : retiradas.length > 0 ? (
        <p className="celula-secundaria">Ao duplicar, a cópia não inclui {retiradas.map(rotuloDaPermissao).join(", ")}.</p>
      ) : null}
      <p className="cartao__texto">
        {perfil.permissoes.length === 0
          ? "Nenhuma permissão."
          : perfil.permissoes.join(", ")}
      </p>
      <p className="celula-secundaria">
        {perfil.usuarios === 0
          ? "Ninguém usa este perfil."
          : `${perfil.usuarios} ${perfil.usuarios === 1 ? "pessoa usa" : "pessoas usam"} este perfil.`}
      </p>
      <div className="mt-2 flex gap-2">
        {aoEditar ? <Botao tamanho="pequeno" variante="fantasma" onClick={aoEditar}>
          Editar
        </Botao> : null}
        {aoApagar ? <Botao tamanho="pequeno" variante="fantasma" disabled={apagando} onClick={aoApagar}>
          Apagar
        </Botao> : null}
        <Botao tamanho="pequeno" variante="fantasma" disabled={duplicando} onClick={aoDuplicar}>
          Duplicar
        </Botao>
      </div>
    </>
  );
}
